import React, { useContext, useEffect } from "react";
import { Container, Button, Row, Col } from "reactstrap";
import { useNavigate, useParams } from "react-router-dom";
import { RETURNS_LOG_PATHS } from "../../../../Routes/paths";
import formStyles from "../../../../assets/scss/common/formStyle.module.scss";
import { useReturnsAccountability } from "../api/useReturnsAccountability";
import SectionCard from "Components/Common/SectionCard";
import { formatDate } from "../../../../common/service/CommonService";
import FullPageLoader from "Components/Common/FullPageLoader";
import DetailField from "../../../../Components/Common/DetailField";
import { BreadcrumbContext } from "Layouts/Header";

const ViewReturnsLog: React.FC = () => {
  const navigate = useNavigate();
  const breadcrumbContext = useContext(BreadcrumbContext);
  const { id } = useParams();

  useEffect(() => {
    breadcrumbContext?.setBreadcrumbItems?.([
      { text: "Returns Log", link: RETURNS_LOG_PATHS.LIST },
      { text: "View Return Log" },
    ]);

    return () => breadcrumbContext?.setBreadcrumbItems?.([]);
  }, []);

  const { SelectQuery } = useReturnsAccountability({
    returnid: Number(id),
  });

  const data = SelectQuery.data?.data?.[0];

  /* ── Loading ── */
  if (SelectQuery.isLoading || SelectQuery.isFetching) {
    return (
      <Container fluid className={`px-0 ${formStyles.pageContainer}`}>
        <FullPageLoader />
      </Container>
    );
  }

  /* ── Not Found ── */
  if (!data) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-center py-5">
            <i className="ri-error-warning-line display-3 text-warning mb-3 d-block" />
            <h4 className="fw-600">Record Not Found</h4>
            <p className="text-muted">
              The return log record you are looking for does not exist or has been removed.
            </p>

          </div>
        </Container>
      </div>
    );
  }
  return (
    <div className="page-content">
      <Container fluid>

        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h3 className="fw-700 mb-1">Return Log Details</h3>
            <p className="text-muted mb-0">
              Return ID: #{data.returnno ?? id}&nbsp;|&nbsp;
              Study: {data.studycode}&nbsp;|&nbsp;
              Date: {data.returndate ? formatDate(data.returndate) : "—"}
            </p>
          </div>

        </div>

        <div className="d-flex flex-column gap-0.5">

          {/* 1 — General Information */}
          <SectionCard
            icon="ri-hospital-line"
            title="General Information"
            subtitle="Return source and protocol details."
          >
            <Row>
              <DetailField label="Study Code" value={data.studycode} columns={3} />
              <DetailField label="Return Date" value={data.returndate ? formatDate(data.returndate) : null} columns={3} />
              <DetailField label="Return Source Type" value={data.returnsourcetypename} columns={3} />
            </Row>
          </SectionCard>

          {/* 2 — Product Information */}
          <SectionCard
            icon="ri-capsule-line"
            title="Investigational Product Details"
            subtitle="Batch and inventory details of the returned product."
          >
            <Row>
              <DetailField label="Product Name" value={data.productname} columns={3} />
              <DetailField label="IP Category" value={data.ipcategory} columns={3} />
              <DetailField label="Batch / Lot Number" value={data.batchlotno} columns={3} />
              <DetailField label="Unit Type" value={data.unittypetext} columns={3} />
            </Row>
          </SectionCard>

          {/* 3 — Return Details */}
          <SectionCard
            icon="ri-survey-line"
            title="Return Details"
            subtitle="Quantities, condition, and reason for this return."
          >
            <Row>
              <DetailField label="Subject No." value={data.subjectno} columns={3} />
              <DetailField label="Quantity Returned" value={data.quantityreturned} columns={3} />
              <DetailField label="Condition of Returned IP" value={data.conditionreturnedtext} columns={3} />
              <DetailField label="Reason for Return" value={data.reasonforreturntext} columns={3} />
              <DetailField label="Comments" value={data.comments} columns={3} />
            </Row>
          </SectionCard>

        </div>
      </Container>
    </div>
  );
};

export default ViewReturnsLog;